import { useEffect, useRef, useState } from "react";

// Direction-aware scroll tracking for the fixed header.
// Returns { direction: "up" | "down", atTop: boolean }.
// - rAF-throttled, passive listener, state only updates on real changes.
// - `threshold` px of movement needed before direction flips (kills jitter).
// - `topOffset` px: below this scrollY the header is forced visible.
// - `resetKey` (e.g. route pathname) resets internal state so a stale "down"
//   state never hides the header on a freshly navigated page.
export default function useScrollDirection({ threshold = 8, topOffset = 112, resetKey } = {}) {
  const readY = () => (typeof window === "undefined" ? 0 : Math.max(0, window.scrollY || window.pageYOffset || 0));
  const [state, setState] = useState(() => ({ direction: "up", atTop: readY() <= topOffset }));
  const lastY = useRef(readY());
  const ticking = useRef(false);
  const stateRef = useRef(state);
  stateRef.current = state;

  useEffect(() => {
    if (typeof window === "undefined") return undefined;

    const commit = (direction, atTop) => {
      const prev = stateRef.current;
      if (prev.direction !== direction || prev.atTop !== atTop) {
        setState({ direction, atTop });
      }
    };

    const update = () => {
      ticking.current = false;
      const y = readY();
      const doc = document.documentElement;
      const maxY = Math.max(0, doc.scrollHeight - window.innerHeight);
      const atBottom = y >= maxY - 1;
      const atTop = y <= topOffset;
      const delta = y - lastY.current;

      let direction = stateRef.current.direction;
      if (Math.abs(delta) >= threshold) {
        // Overscroll bounce past the footer must not register as "down".
        if (delta > 0 && !atBottom) direction = "down";
        else if (delta < 0) direction = "up";
        lastY.current = y;
      }
      if (atTop) direction = "up";
      commit(direction, atTop);
    };

    const onScroll = () => {
      if (ticking.current) return;
      ticking.current = true;
      window.requestAnimationFrame(update);
    };

    // Sync with the current position (mid-page reload, StrictMode remount).
    lastY.current = readY();
    update();

    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
    };
  }, [threshold, topOffset]);

  // Route change: RouteManager scrolls to the new position synchronously in a
  // layout effect, so by this effect the scrollY is already correct.
  useEffect(() => {
    if (typeof window === "undefined") return;
    lastY.current = readY();
    const atTop = lastY.current <= topOffset;
    setState((prev) => (prev.direction === "up" && prev.atTop === atTop ? prev : { direction: "up", atTop }));
  }, [resetKey, topOffset]);

  return state;
}
